import { Link } from 'react-router-dom';
import { Document } from '@/db/types/document';
import { DecoratedClient } from '@/db/types/client';
import DocumentStatusPill from '../DocumentStatusPill';
import { documentPath } from '@/utils/routes';
import { formatDate } from '@/utils/date_utils';
import { UserIcon } from '@heroicons/react/24/outline';

function LoadingList() {
  return <div className="py-8 text-center text-gray-500">Loading...</div>;
}

function EmptyList() {
  return <div className="py-8 text-center text-gray-500">No recent documents</div>;
}

export interface RecentDocumentsListContentProps {
  documents: Document[] | undefined;
  clientsMap: Map<number, DecoratedClient> | undefined;
  templatesMap: Map<number, { name: string }> | undefined;
}

function DocumentListContent({ documents, clientsMap, templatesMap }: RecentDocumentsListContentProps) {
  if (documents === undefined || clientsMap === undefined || templatesMap === undefined) return <LoadingList />;
  if (documents.length === 0) return <EmptyList />;

  return <>{documents.map((document, index) => {
    const client = clientsMap.get(document.client_id);
    const template = templatesMap.get(document.template_id);
    const path = client ? documentPath(client.label, document) : undefined;
    const key = document.id ?? document.local_id ?? `doc-${index}`;
    const content = (
      <>
        <div className="flex-[2] min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {client?.person.displayName || `Client ${document.client_id}`}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <UserIcon className="w-3 h-3" />
            {template?.name || 'Document'}
          </div>
        </div>
        <div className="flex-1 flex flex-col items-end gap-1">
          <div className="text-xs text-gray-500 whitespace-nowrap">
            {formatDate(document.updated_at)}
          </div>
          {/* Same chip as the client Documents tab, so a document deleted online
              (or in any sync-error state) never masquerades under its stale status */}
          <DocumentStatusPill document={document} />
        </div>
      </>
    );

    return path ? (
      <Link
        key={key}
        to={path}
        className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
      >
        {content}
      </Link>
    ) : (
      <div
        key={key}
        className="flex items-start gap-3 px-4 py-3 border-b border-gray-100"
      >
        {content}
      </div>
    );
  })}</>;
}

export function RecentDocumentsListContent(props: RecentDocumentsListContentProps) {
  return (
    <div className="flex flex-col min-h-0 flex-1">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Docs (Past 30 days)</h2>
      <div className="bg-white rounded-lg border border-gray-200 flex-1 min-h-0 overflow-y-auto shadow-sm">
        <DocumentListContent {...props} />
      </div>
    </div>
  );
}

